import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Plus,
  Warehouse,
  Search,
  AlertTriangle,
  ArrowRightLeft,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PageHeader } from "@/components/PageHeader";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/lib/auth-context";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";
import { format } from "date-fns";

interface Position {
  id: string;
  productId: string;
  inventoryLocationId: string;
  lotNumber: string | null;
  expirationDate: string | null;
  uom: string;
  quantityOnHand: string;
  quantityCommitted: string;
  quantityInTransit: string;
  quantityAvailable: string | null;
  reorderPoint: string | null;
  safetyStock: string | null;
  unitCost: string | null;
  product?: { id: string; name: string; sku: string | null };
  location?: { id: string; locationCode: string; locationType: string };
  warehouse?: { id: string; name: string; warehouseCode: string };
}

interface InvLocation {
  id: string;
  locationCode: string;
  locationType: string;
  warehouseId: string | null;
  custodyVendorId: string | null;
  status: string;
  notes: string | null;
  warehouse?: { id: string; name: string; warehouseCode: string };
}

interface Movement {
  id: string;
  inventoryPositionId: string;
  movementType: string;
  quantityDelta: string;
  uom: string;
  affectsDimension: string;
  lotNumber: string | null;
  notes: string | null;
  occurredAt: string;
  product?: { id: string; name: string; sku: string | null };
  location?: { id: string; locationCode: string };
}

interface PositionsResp {
  positions: Position[];
}
interface LocationsResp {
  locations: InvLocation[];
}
interface MovementsResp {
  movements: Movement[];
}
interface Warehouse {
  id: string;
  name: string;
  warehouseCode: string;
}
interface Product {
  id: string;
  name: string;
  code: string;
}

export default function InventoryPage() {
  const { sessionId } = useAuth();
  const [activeTab, setActiveTab] = useState("positions");
  const [search, setSearch] = useState("");
  const [warehouseFilter, setWarehouseFilter] = useState("all");
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [expandedWarehouses, setExpandedWarehouses] = useState<Record<string, boolean>>({});
  const [adjustDrawerOpen, setAdjustDrawerOpen] = useState(false);
  const [transferDrawerOpen, setTransferDrawerOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: positionsData, isLoading: positionsLoading } = useQuery<PositionsResp>({
    queryKey: ["/api/inventory/positions", { search, warehouseFilter, lowStockOnly }],
    queryFn: () => {
      const params = new URLSearchParams();
      if (search) params.set("search", search);
      if (warehouseFilter !== "all") params.set("warehouseId", warehouseFilter);
      if (lowStockOnly) params.set("lowStock", "true");
      const qs = params.toString();
      return api.get<PositionsResp>(
        `/inventory/positions${qs ? `?${qs}` : ""}`,
        sessionId,
      );
    },
  });

  const { data: locationsData, isLoading: locationsLoading } = useQuery<LocationsResp>({
    queryKey: ["/api/inventory/locations"],
    queryFn: () => api.get<LocationsResp>("/inventory/locations", sessionId),
    enabled: activeTab === "locations",
  });

  const { data: movementsData, isLoading: movementsLoading } = useQuery<MovementsResp>({
    queryKey: ["/api/inventory/movements"],
    queryFn: () => api.get<MovementsResp>("/inventory/movements", sessionId),
    enabled: activeTab === "movements",
  });

  const { data: warehouses = [] } = useQuery<Warehouse[]>({
    queryKey: ["/api/warehouses"],
    queryFn: () => api.get<Warehouse[]>("/warehouses", sessionId),
  });

  const { data: products = [] } = useQuery<{ products: Product[] }>({
    queryKey: ["/api/products", "for-inventory"],
    queryFn: () => api.get("/products?limit=500", sessionId),
    enabled: adjustDrawerOpen || transferDrawerOpen,
  });

  const positions = positionsData?.positions || [];
  const locations = locationsData?.locations || [];
  const movements = movementsData?.movements || [];

  const groupedByWarehouse = useMemo(() => {
    const map = new Map<string, { warehouse: { id: string; name: string; warehouseCode: string }; positions: Position[] }>();
    for (const p of positions) {
      const key = p.warehouse?.id || "_unassigned";
      if (!map.has(key)) {
        map.set(key, {
          warehouse: p.warehouse || { id: "_unassigned", name: "Unassigned", warehouseCode: "—" },
          positions: [],
        });
      }
      map.get(key)!.positions.push(p);
    }
    return Array.from(map.values());
  }, [positions]);

  const toggleWarehouse = (id: string) => {
    setExpandedWarehouses((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Adjust form state
  const [adjForm, setAdjForm] = useState({
    productId: "",
    inventoryLocationId: "",
    lotNumber: "",
    uom: "each",
    quantityDelta: "",
    notes: "",
  });
  const adjustMutation = useMutation({
    mutationFn: () =>
      api.post(
        "/inventory/movements",
        {
          productId: adjForm.productId,
          inventoryLocationId: adjForm.inventoryLocationId,
          lotNumber: adjForm.lotNumber || null,
          uom: adjForm.uom,
          movementType: "adjustment",
          affectsDimension: "on_hand",
          quantityDelta: adjForm.quantityDelta,
          notes: adjForm.notes,
        },
        sessionId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/inventory/positions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/inventory/movements"] });
      toast({ title: "Adjustment recorded" });
      setAdjustDrawerOpen(false);
      setAdjForm({
        productId: "",
        inventoryLocationId: "",
        lotNumber: "",
        uom: "each",
        quantityDelta: "",
        notes: "",
      });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  // Transfer form state
  const [transForm, setTransForm] = useState({
    inventoryPositionId: "",
    productId: "",
    transferFromLocationId: "",
    transferToLocationId: "",
    quantity: "",
    notes: "",
  });
  const transferMutation = useMutation({
    mutationFn: () =>
      api.post(
        "/inventory/movements",
        {
          productId: transForm.productId,
          inventoryLocationId: transForm.transferFromLocationId,
          movementType: "transfer_out",
          affectsDimension: "on_hand",
          quantityDelta: `-${transForm.quantity}`,
          transferToLocationId: transForm.transferToLocationId,
          notes: transForm.notes,
        },
        sessionId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/inventory/positions"] });
      queryClient.invalidateQueries({ queryKey: ["/api/inventory/movements"] });
      toast({ title: "Transfer recorded" });
      setTransferDrawerOpen(false);
      setTransForm({
        inventoryPositionId: "",
        productId: "",
        transferFromLocationId: "",
        transferToLocationId: "",
        quantity: "",
        notes: "",
      });
    },
    onError: (e: Error) =>
      toast({ title: "Error", description: e.message, variant: "destructive" }),
  });

  const isLowStock = (p: Position) => {
    if (!p.reorderPoint || !p.quantityAvailable) return false;
    return Number(p.quantityAvailable) <= Number(p.reorderPoint);
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Inventory"
        description="Track stock positions, locations, and movements"
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setTransferDrawerOpen(true)}>
              <ArrowRightLeft className="h-4 w-4 mr-2" />
              Transfer
            </Button>
            <Button onClick={() => setAdjustDrawerOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              Adjust Quantity
            </Button>
          </div>
        }
      />

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="positions">Positions</TabsTrigger>
          <TabsTrigger value="locations">Locations</TabsTrigger>
          <TabsTrigger value="movements">Movements</TabsTrigger>
        </TabsList>

        <TabsContent value="positions" className="space-y-4">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by product or lot..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="All warehouses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All warehouses</SelectItem>
                {warehouses.map((w) => (
                  <SelectItem key={w.id} value={w.id}>
                    {w.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              variant={lowStockOnly ? "default" : "outline"}
              onClick={() => setLowStockOnly(!lowStockOnly)}
              size="sm"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Low Stock Only
            </Button>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">
                    Product
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">
                    Location
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">
                    Lot
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide">
                    Expires
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide text-right">
                    On Hand
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide text-right">
                    Committed
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide text-right">
                    In Transit
                  </TableHead>
                  <TableHead className="text-xs font-medium uppercase tracking-wide text-right">
                    Available
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {positionsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : positions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <Warehouse className="h-12 w-12 text-muted-foreground/50 mx-auto mb-2" />
                      <p className="text-muted-foreground">No inventory positions</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  groupedByWarehouse.map(({ warehouse, positions: whPositions }) => {
                    const isExpanded = expandedWarehouses[warehouse.id] ?? true;
                    const totalOnHand = whPositions.reduce(
                      (sum, p) => sum + Number(p.quantityOnHand || 0),
                      0,
                    );
                    return (
                      <>
                        <TableRow
                          key={`group-${warehouse.id}`}
                          className="cursor-pointer bg-muted/50 hover:bg-muted/70"
                          onClick={() => toggleWarehouse(warehouse.id)}
                        >
                          <TableCell colSpan={8}>
                            <div className="flex items-center gap-2 py-0.5">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                              )}
                              <span className="font-semibold text-sm">
                                {warehouse.name}
                              </span>
                              <Badge variant="outline" className="text-xs">
                                {warehouse.warehouseCode}
                              </Badge>
                              <Badge variant="secondary">
                                {whPositions.length}
                              </Badge>
                              <span className="text-xs text-muted-foreground ml-auto">
                                {totalOnHand.toLocaleString()} on hand
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                        {isExpanded &&
                          whPositions.map((p) => {
                            const low = isLowStock(p);
                            return (
                              <TableRow key={p.id} className={cn(low && "bg-red-50/40")}>
                                <TableCell>
                                  <div className="font-medium">
                                    {p.product?.name || "—"}
                                  </div>
                                  {p.product?.sku && (
                                    <div className="text-xs text-muted-foreground">
                                      {p.product.sku}
                                    </div>
                                  )}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {p.location?.locationCode || "—"}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {p.lotNumber || "—"}
                                </TableCell>
                                <TableCell className="text-sm">
                                  {p.expirationDate
                                    ? format(new Date(p.expirationDate), "MMM d, yyyy")
                                    : "—"}
                                </TableCell>
                                <TableCell className="text-right">
                                  {Number(p.quantityOnHand).toLocaleString()}
                                </TableCell>
                                <TableCell className="text-right">
                                  {Number(p.quantityCommitted).toLocaleString()}
                                </TableCell>
                                <TableCell className="text-right">
                                  {Number(p.quantityInTransit).toLocaleString()}
                                </TableCell>
                                <TableCell
                                  className={cn(
                                    "text-right font-medium",
                                    low && "text-red-600",
                                  )}
                                >
                                  {p.quantityAvailable != null
                                    ? Number(p.quantityAvailable).toLocaleString()
                                    : "—"}
                                  {low && (
                                    <AlertTriangle className="inline h-3.5 w-3.5 ml-1 text-red-600" />
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                      </>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="locations" className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Location Code</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Warehouse</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {locationsLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : locations.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-12 text-muted-foreground">
                      No locations defined
                    </TableCell>
                  </TableRow>
                ) : (
                  locations.map((loc) => (
                    <TableRow key={loc.id}>
                      <TableCell className="font-medium">
                        {loc.locationCode}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{loc.locationType}</Badge>
                      </TableCell>
                      <TableCell>{loc.warehouse?.name || "—"}</TableCell>
                      <TableCell>
                        <Badge
                          className={cn(
                            loc.status === "ACTIVE"
                              ? "bg-green-600 text-white"
                              : "bg-gray-500 text-white",
                          )}
                        >
                          {loc.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {loc.notes || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="movements" className="space-y-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Occurred</TableHead>
                  <TableHead>Product</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Dimension</TableHead>
                  <TableHead className="text-right">Delta</TableHead>
                  <TableHead>Lot</TableHead>
                  <TableHead>Notes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movementsLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : movements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                      No movements recorded
                    </TableCell>
                  </TableRow>
                ) : (
                  movements.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="text-sm whitespace-nowrap">
                        {format(new Date(m.occurredAt), "MMM d, yyyy HH:mm")}
                      </TableCell>
                      <TableCell>{m.product?.name || "—"}</TableCell>
                      <TableCell>{m.location?.locationCode || "—"}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{m.movementType}</Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {m.affectsDimension}
                      </TableCell>
                      <TableCell
                        className={cn(
                          "text-right font-medium",
                          Number(m.quantityDelta) < 0
                            ? "text-red-600"
                            : "text-green-600",
                        )}
                      >
                        {Number(m.quantityDelta) > 0 ? "+" : ""}
                        {Number(m.quantityDelta).toLocaleString()} {m.uom}
                      </TableCell>
                      <TableCell>{m.lotNumber || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground max-w-[200px] truncate">
                        {m.notes || "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Adjust Quantity Drawer */}
      <Sheet open={adjustDrawerOpen} onOpenChange={setAdjustDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Adjust Quantity</SheetTitle>
            <SheetDescription>
              Record an inventory adjustment for a product.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product</Label>
              <Select
                value={adjForm.productId}
                onValueChange={(v) => setAdjForm((prev) => ({ ...prev, productId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {(products as any).products?.map((p: Product) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.code ? ` (${p.code})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Inventory Location</Label>
              <Select
                value={adjForm.inventoryLocationId}
                onValueChange={(v) => setAdjForm((prev) => ({ ...prev, inventoryLocationId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem key={l.id} value={l.id}>
                      {l.locationCode}{l.warehouse ? ` — ${l.warehouse.name}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Lot Number (optional)</Label>
                <Input
                  value={adjForm.lotNumber}
                  onChange={(e) => setAdjForm((prev) => ({ ...prev, lotNumber: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Quantity Delta *</Label>
                <Input
                  type="number"
                  placeholder="e.g. 10 or -5"
                  value={adjForm.quantityDelta}
                  onChange={(e) => setAdjForm((prev) => ({ ...prev, quantityDelta: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={adjForm.notes}
                onChange={(e) => setAdjForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setAdjustDrawerOpen(false)}
                disabled={adjustMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => adjustMutation.mutate()}
                disabled={
                  adjustMutation.isPending ||
                  !adjForm.productId ||
                  !adjForm.inventoryLocationId ||
                  !adjForm.quantityDelta
                }
              >
                {adjustMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Record Adjustment
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      {/* Transfer Drawer */}
      <Sheet open={transferDrawerOpen} onOpenChange={setTransferDrawerOpen}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader>
            <SheetTitle>Transfer Inventory</SheetTitle>
            <SheetDescription>
              Move stock between inventory locations.
            </SheetDescription>
          </SheetHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Product</Label>
              <Select
                value={transForm.productId}
                onValueChange={(v) => setTransForm((prev) => ({ ...prev, productId: v }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select product" />
                </SelectTrigger>
                <SelectContent>
                  {(products as any).products?.map((p: Product) => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.name}{p.code ? ` (${p.code})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>From Location *</Label>
                <Select
                  value={transForm.transferFromLocationId}
                  onValueChange={(v) => setTransForm((prev) => ({ ...prev, transferFromLocationId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Origin" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.locationCode}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>To Location *</Label>
                <Select
                  value={transForm.transferToLocationId}
                  onValueChange={(v) => setTransForm((prev) => ({ ...prev, transferToLocationId: v }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Destination" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations
                      .filter((l) => l.id !== transForm.transferFromLocationId)
                      .map((l) => (
                        <SelectItem key={l.id} value={l.id}>
                          {l.locationCode}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Quantity *</Label>
              <Input
                type="number"
                min="0"
                value={transForm.quantity}
                onChange={(e) => setTransForm((prev) => ({ ...prev, quantity: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={transForm.notes}
                onChange={(e) => setTransForm((prev) => ({ ...prev, notes: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setTransferDrawerOpen(false)}
                disabled={transferMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={() => transferMutation.mutate()}
                disabled={
                  transferMutation.isPending ||
                  !transForm.productId ||
                  !transForm.transferFromLocationId ||
                  !transForm.transferToLocationId ||
                  !transForm.quantity
                }
              >
                {transferMutation.isPending && (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                )}
                Transfer
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
